import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { PageHeader } from '@/components/ui/page-header'
import { DeviceRequestModal } from '@/components/modals/DeviceRequestModal'
import { ReviewDeviceRequestModal } from '@/components/modals/ReviewDeviceRequestModal'
import { apiClient } from '@/lib/api-client'
import { useAuth } from '@/hooks/useAuth'
import { getRelativeTime } from '@shared/utils'
import { toast } from 'react-hot-toast'
import { cn } from '@/lib/utils'

const filters = [
  { value: 'pending', label: 'Open' },
  { value: 'fulfilled', label: 'Assigned' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
]

function typeLabel(type?: string) {
  if (type === 'damage') return 'Damage'
  if (type === 'replace') return 'Replace'
  return 'New'
}

function statusBadge(status?: string) {
  if (status === 'pending') return <Badge variant="warning">Open</Badge>
  if (status === 'fulfilled') return <Badge variant="success">Assigned</Badge>
  if (status === 'rejected') return <Badge variant="destructive">Rejected</Badge>
  if (status === 'cancelled') return <Badge variant="outline">Cancelled</Badge>
  return <Badge variant="outline">{status}</Badge>
}

export function DeviceRequestsPage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'Admin'
  const queryClient = useQueryClient()
  const [filter, setFilter] = useState('pending')
  const [composeOpen, setComposeOpen] = useState(false)
  const [review, setReview] = useState<any>(null)
  const [cancellingId, setCancellingId] = useState<number | null>(null)

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['device-requests', filter, isAdmin],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (filter !== 'all') params.status = filter
      const response = await apiClient.get('/device-requests', { params })
      return response.data
    },
  })

  const cancel = async (requestId: number) => {
    setCancellingId(requestId)
    try {
      await apiClient.post(`/device-requests/${requestId}/cancel`)
      queryClient.invalidateQueries({ queryKey: ['device-requests'] })
      toast.success('Request cancelled')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Could not cancel the request')
    } finally {
      setCancellingId(null)
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Device requests"
        actions={
          <Button onClick={() => setComposeOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Request a device
          </Button>
        }
      />

      <div className="data-panel">
        <div className="flex flex-wrap gap-1 border-b border-border px-3 py-3">
          {filters.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => setFilter(item.value)}
              className={cn(
                'h-8 rounded-md px-3 text-sm',
                filter === item.value
                  ? 'bg-primary/10 font-medium text-primary'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {item.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : requests.length === 0 ? (
          <div className="px-4 py-16 text-center">
            <p className="text-sm font-medium">No requests in this view</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Ask for a new device, a replacement, or report a damaged one.
            </p>
            <Button className="mt-4" onClick={() => setComposeOpen(true)}>
              Request a device
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Needed</th>
                  <th>Type</th>
                  <th>Current asset</th>
                  {isAdmin && <th>From</th>}
                  <th>Status</th>
                  <th>When</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request: any) => {
                  const current = request.currentDevice
                  const assigned = request.assignedDevice
                  return (
                    <tr key={request.id}>
                      <td>
                        <p className="font-medium">{request.deviceName}</p>
                        <p className="max-w-xs truncate text-xs text-muted-foreground">{request.reason}</p>
                      </td>
                      <td data-label="Type">{typeLabel(request.requestType)}</td>
                      <td data-label="Current asset">
                        {current ? (
                          isAdmin ? (
                            <Link to={`/devices/${current.id}`} className="font-mono text-xs text-primary hover:underline">
                              {current.serialNumber || current.name}
                            </Link>
                          ) : (
                            <span className="font-mono text-xs">{current.serialNumber || current.name}</span>
                          )
                        ) : assigned ? (
                          <span className="text-xs text-muted-foreground">
                            Given{' '}
                            {isAdmin ? (
                              <Link to={`/devices/${assigned.id}`} className="font-mono text-primary hover:underline">
                                {assigned.serialNumber || assigned.name}
                              </Link>
                            ) : (
                              <span className="font-mono">{assigned.serialNumber || assigned.name}</span>
                            )}
                          </span>
                        ) : '—'}
                      </td>
                      {isAdmin && <td data-label="From">{request.requester?.name || '—'}</td>}
                      <td data-label="Status">{statusBadge(request.status)}</td>
                      <td data-label="When" className="text-muted-foreground">{getRelativeTime(request.createdAt)}</td>
                      <td className="is-actions">
                        <div className="flex justify-end gap-1">
                          {isAdmin && request.status === 'pending' && (
                            <Button size="sm" className="h-8" onClick={() => setReview(request)}>
                              Review
                            </Button>
                          )}
                          {request.status === 'pending' && request.requestedBy === user?.id && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8"
                              disabled={cancellingId === request.id}
                              onClick={() => cancel(request.id)}
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DeviceRequestModal isOpen={composeOpen} onClose={() => setComposeOpen(false)} />
      <ReviewDeviceRequestModal request={review} onClose={() => setReview(null)} />
    </div>
  )
}
